<template>
    <div :class="[$style.search_form_input, props.short && $style.short]">
        <Icon
            name="search"
            :class="$style.icon"
        />
        <input
            id="search"
            ref="input"
            v-model="query"
            type="search"
            :placeholder="props.placeholder"
            :class="$style.input"
            autocomplete="off"
            :style="{
                '--digits': props.placeholder?.length || 0,
            }"
            inputmode="search"
            @input="onInput"
            @focus="$emit('focus')"
        />
    </div>
</template>

<script setup lang="ts">
import { useDebounceFn, onKeyStroke } from '@vueuse/core'
import { computed, ref, useTemplateRef, watch } from 'vue'

import Icon from '@/components/Icon/Icon.vue'

const props = defineProps<{
    placeholder: string
    buttonText: string
    short?: boolean
}>()

const emit = defineEmits<{ focus: []; 'update:modelValue': [value: string]; enter: [value: string] }>()

const inputRef = useTemplateRef('input')
const query = ref('')
const disabledButton = ref(true)

const isShort = computed(() => props.short)

const debouncedEmit = useDebounceFn((value: string) => {
    emit('update:modelValue', value)
}, 500)

watch(isShort, (value) => {
    if (value) {
        query.value = ''
        disabledButton.value = true
        emit('update:modelValue', query.value)
    }
})

function focus() {
    inputRef.value?.focus()
}

defineExpose({
    focus,
})

function onInput(e: Event) {
    const target = e.target as HTMLInputElement
    disabledButton.value = query.value.trim().length <= 1
    if (target.value === '') {
        emit('update:modelValue', '')
    } else {
        debouncedEmit(query.value)
    }
}

onKeyStroke('Enter', () => {
    if (query.value.trim().length > 0) {
        emit('enter', query.value.trim())
    }
})
</script>

<style module>
.search_form_input {
    --bg: var(--carbon-400);
    --border-color-form: transparent;
    --text: var(--white);
    --icon-color: var(--white-60);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg);
    outline: 1px solid var(--border-color-form);
    border-radius: 1000px;
    padding: 4px 4px 4px 16px;
    box-sizing: border-box;
    width: 100%;
    flex: 1;
    position: relative;
    transition: box-shadow 0.3s;
    height: 56px;
}

.icon {
    width: 30px;
    height: 30px;
    color: var(--icon-color);
    flex: 0 0 auto;
    margin-right: 4rem;
}

.search_form_input:focus-within {
    --bg: transparent;
    --border-color-form: var(--purple-300);
}

.input {
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
    color: var(--text);
    padding-right: 4px;
    font-size: var(--font-size-label-l);
    line-height: var(--font-size-label-l);
    caret-color: var(--purple-400);
    box-sizing: content-box;
    height: 36px;
}

.search_form_input:hover {
    --border-color-form: var(--purple-300);
    box-shadow: 0 0 30px 0 rgba(163, 109, 253, 0.64) inset;
}

.search_form_input:focus-within .icon {
    --icon-color: var(--purple-600);
}

.input::placeholder {
    color: var(--white-60);
}

.search_form_input.short {
    padding: 8px;
    width: 56px;
}

.search_form_input.short .input {
    position: absolute;
    width: 34px;
    height: 32px;
}

.search_form_input.short .icon {
    margin: 0;
}

.search_form_input.short .input::placeholder,
.search_form_input:not(.short) .input::placeholder {
    color: transparent;
}

@media (--desktop-medium-plus) {
    .search_form_input {
        justify-content: start;
    }

    .search_form_input.short {
        width: 210px;
        padding: 8px 10px 8px 16px;
    }

    .search_form_input.short .input {
        width: calc(var(--digits) * 1ch);
        padding: 0;
        position: static;
        height: auto;
    }
    .search_form_input.short .input::placeholder {
        color: var(--white-60);
    }

    .search_form_input.short .icon {
        margin-right: 4px;
    }
}
</style>
